import { useState } from 'react';
import { useMarketingData } from '../MarketingContext';

export default function StrategyPage() {
  const { companies, projects } = useMarketingData();
  const [selectedYear, setSelectedYear] = useState(1);


  const years = [
    { num: 1, label: 'Year 1: Foundation', period: 'Sep 2026 – Aug 2027', color: 'blue' },
    { num: 2, label: 'Year 2: Growth', period: 'Sep 2027 – Aug 2028', color: 'green' },
    { num: 3, label: 'Year 3: Scale & Optimize', period: 'Sep 2028 – Aug 2029', color: 'purple' },
  ];

  const yearActivities: Record<number, { quarter: string; items: { name: string; status: string; priority: string }[] }[]> = {
    1: [
      { quarter: 'Q1: Setup & Research', items: [
        { name: 'Market Research & Customer Analysis', status: 'in_progress', priority: 'high' },
        { name: 'Competitor Digital Audit', status: 'completed', priority: 'high' },
        { name: 'Brand Positioning Workshop', status: 'planning', priority: 'high' },
        { name: 'Buyer Persona Development', status: 'planning', priority: 'medium' },
        { name: 'Website Redesign & Launch', status: 'in_progress', priority: 'critical' },
        { name: 'Google Analytics + GTM Setup', status: 'not_started', priority: 'high' },
      ]},
      { quarter: 'Q2: SEO & Content Foundation', items: [
        { name: 'SEO Audit & Keyword Research', status: 'in_progress', priority: 'high' },
        { name: 'Technical SEO Fixes', status: 'not_started', priority: 'high' },
        { name: 'Content Strategy & Calendar', status: 'in_progress', priority: 'medium' },
        { name: 'Blog Launch (8 articles)', status: 'not_started', priority: 'medium' },
        { name: 'Social Media Setup (LinkedIn, YouTube)', status: 'not_started', priority: 'medium' },
        { name: 'Google My Business Optimization', status: 'not_started', priority: 'high' },
      ]},
      { quarter: 'Q3: Initial Campaigns', items: [
        { name: 'Google Ads — Castor Wheels', status: 'planned', priority: 'high' },
        { name: 'LinkedIn Thought Leadership', status: 'planned', priority: 'medium' },
        { name: 'Email Marketing Setup', status: 'not_started', priority: 'medium' },
        { name: 'IndiaMART Premium Listing', status: 'not_started', priority: 'high' },
        { name: 'Product Photography & Video', status: 'not_started', priority: 'medium' },
      ]},
      { quarter: 'Q4: Optimize & Scale', items: [
        { name: 'SEO Content Scaling (16 articles total)', status: 'not_started', priority: 'high' },
        { name: 'PPC Campaign Optimization', status: 'not_started', priority: 'high' },
        { name: 'Lead Nurturing Sequences', status: 'not_started', priority: 'medium' },
        { name: 'Year 1 Review & Planning', status: 'not_started', priority: 'high' },
      ]},
    ],
    2: [
      { quarter: 'Q1: Channel Expansion', items: [
        { name: 'YouTube Channel Launch', status: 'not_started', priority: 'medium' },
        { name: 'Case Study Program (5 stories)', status: 'not_started', priority: 'high' },
        { name: 'Industry Directory Listings', status: 'not_started', priority: 'medium' },
        { name: 'Trade Show Digital Integration', status: 'not_started', priority: 'low' },
      ]},
      { quarter: 'Q2: Content Scaling', items: [
        { name: 'SEO Content at Scale (40+ articles)', status: 'not_started', priority: 'high' },
        { name: 'Video Content Series', status: 'not_started', priority: 'medium' },
        { name: 'Webinar Program Launch', status: 'not_started', priority: 'medium' },
        { name: 'Interactive Tools (Load Calculator)', status: 'not_started', priority: 'high' },
      ]},
      { quarter: 'Q3: Paid Advertising Optimization', items: [
        { name: 'Google Ads Expansion', status: 'not_started', priority: 'high' },
        { name: 'LinkedIn Ads Campaign', status: 'not_started', priority: 'medium' },
        { name: 'Retargeting Campaigns', status: 'not_started', priority: 'medium' },
        { name: 'Conversion Rate Optimization', status: 'not_started', priority: 'high' },
      ]},
      { quarter: 'Q4: Automation & Partnerships', items: [
        { name: 'Marketing Automation Setup', status: 'not_started', priority: 'high' },
        { name: 'Customer Segmentation', status: 'not_started', priority: 'medium' },
        { name: 'Strategic Partnership Program', status: 'not_started', priority: 'medium' },
        { name: 'Year 2 Review & Planning', status: 'not_started', priority: 'high' },
      ]},
    ],
    3: [
      { quarter: 'Q1: Market Expansion', items: [
        { name: 'Export Market Digital Strategy', status: 'not_started', priority: 'high' },
        { name: 'Multi-language Content', status: 'not_started', priority: 'low' },
        { name: 'Advanced SEO (Schema, Featured Snippets)', status: 'not_started', priority: 'high' },
      ]},
      { quarter: 'Q2: Advanced Marketing', items: [
        { name: 'AI-Powered Personalization', status: 'not_started', priority: 'medium' },
        { name: 'Account-Based Marketing', status: 'not_started', priority: 'high' },
        { name: 'Customer Loyalty Program', status: 'not_started', priority: 'medium' },
      ]},
      { quarter: 'Q3: Optimization', items: [
        { name: 'Revenue Attribution Model', status: 'not_started', priority: 'high' },
        { name: 'Marketing Efficiency Analysis', status: 'not_started', priority: 'medium' },
        { name: 'Competitive Moat Building', status: 'not_started', priority: 'high' },
      ]},
      { quarter: 'Q4: Brand Leadership', items: [
        { name: 'Industry Thought Leadership Position', status: 'not_started', priority: 'high' },
        { name: 'Brand Authority Campaign', status: 'not_started', priority: 'high' },
        { name: 'Year 3 Review & Next Strategy', status: 'not_started', priority: 'high' },
      ]},
    ],
  };

  const statusColors: Record<string, string> = {
    completed: 'bg-green-100 text-green-700 border-green-200',
    in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
    planning: 'bg-amber-100 text-amber-700 border-amber-200',
    planned: 'bg-purple-100 text-purple-700 border-purple-200',
    not_started: 'bg-gray-100 text-gray-500 border-gray-200',
  };
  const priorityColors: Record<string, string> = {
    critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700',
    medium: 'bg-blue-100 text-blue-700', low: 'bg-gray-100 text-gray-500',
  };

  const allActivities = yearActivities[selectedYear] || [];
  const totalItems = allActivities.reduce((s, q) => s + q.items.length, 0);
  const completedItems = allActivities.reduce((s, q) => s + q.items.filter(i => i.status === 'completed').length, 0);
  const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Three-Year Marketing Strategy</h1>
        <p className="text-sm text-gray-500">Strategic roadmap for all companies — Foundation → Growth → Scale</p>
      </div>

      {/* Year Selector */}
      <div className="grid grid-cols-3 gap-3">
        {years.map(y => (
          <button key={y.num} onClick={() => setSelectedYear(y.num)} className={`p-4 rounded-xl border-2 text-left transition-all ${selectedYear === y.num ? `border-${y.color}-400 bg-${y.color}-50 shadow-md` : 'border-gray-200 bg-white hover:border-gray-300'}`}>
            <div className="text-sm font-bold text-gray-900">{y.label}</div>
            <div className="text-xs text-gray-500 mt-0.5">{y.period}</div>
            {selectedYear === y.num && <div className="text-xs text-blue-600 mt-2 font-medium">{progress}% complete</div>}
          </button>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-900">Year {selectedYear} Progress</span>
          <span className="text-sm text-gray-500">{completedItems}/{totalItems} activities completed</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Quarters */}
      <div className="space-y-4">
        {allActivities.map((quarter, qi) => (
          <div key={qi} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-900">{quarter.quarter}</h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {quarter.items.map((item, ii) => (
                <div key={ii} className={`p-3 rounded-lg border ${statusColors[item.status] || 'bg-gray-50'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs font-medium">{item.name}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${priorityColors[item.priority]}`}>{item.priority}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Company Projects */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Active Projects</h3>
        <div className="space-y-2">
          {projects.filter(p => p.status === 'in_progress' || p.status === 'planning').map(p => {
            const co = companies.find(c => c.id === p.companyId);
            return (
              <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-800">{p.name}</div>
                  <div className="text-xs text-gray-500">🏢 {co?.name} · Budget: ₹{(p.budget / 100000).toFixed(1)}L</div>
                </div>
                <div className="flex gap-1">
                  {p.milestones.map((m, i) => <div key={i} className={`h-2 w-6 rounded-full ${m.completed ? 'bg-green-400' : 'bg-gray-200'}`} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
